<template>
  <div ref="composerRef" class="chat-input-card" :style="{ '--input-brand-color': color }">
    <template v-if="!cStore.sendDisable">
      <Transition name="fade-scale">
        <InputActionMenu
          v-show="sStore.functionListOpen"
          @close="sStore.handleFuncClose"
          @image-success="handleSendImage"
          @file-success="handleSendFile"
        />
      </Transition>

      <Transition name="fade-scale">
        <EmotionModal v-if="sStore.emotionModalOpen" />
      </Transition>

      <div class="chat-input-wrapper">
        <div class="input-control-anchor">
          <FunctionButton :label="$t('session.chat.more')" @func-open="handleFunction" />
        </div>

        <div class="input-control-anchor">
          <EmotionButton :label="$t('session.chat.emoji')" @emotion-open="handleEmotion" />
        </div>

        <textarea
          ref="textareaRef"
          v-model="cStore.chatContent"
          rows="1"
          class="chat-textarea"
          :placeholder="$t('session.chat.button.placeholder')"
          @keydown="handleKeydown"
          @input="autoResize"
          @compositionstart="handleCompositionStart"
          @compositionend="handleCompositionEnd"
          @focus="closePopovers"
        ></textarea>

        <button
          type="button"
          class="send-button"
          :class="{ active: hasContent }"
          :disabled="!hasContent"
          :aria-label="$t('common.send')"
          @click="handleSend"
        >
          <SendIcon color="#fff" />
        </button>
      </div>
    </template>

    <div v-else-if="cStore.role === 'agent'" class="agent-offline-pill">
      <span>{{ $t('widget.agent.offline.desc') }}</span>
    </div>

    <div v-else class="rechat-pill">
      <span>{{ $t('session.rechat.desc') }}</span>
      <button type="button" @click="handleRestart">
        {{ $t('session.rechat.button') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * Purpose: Own the unified desktop/mobile Widget composer and its ended-session replacement.
 * State: Shared setting-store flags control mutually exclusive action and emoji popovers. The
 * action menu stays mounted while hidden so asynchronous upload callbacks and its one-time grade
 * configuration survive repeated opens; the chat store owns the draft and session-disabled state.
 * Props: color is the normalized channel brand color used by the enabled send-button and focus
 * states. The disabled fill is derived from the same brand color by the dialog-level CSS tokens.
 */
import { computed, defineComponent, ref, watch } from 'vue'
import type { ChatFileUploadResult, ChatImageUploadData } from '../../../hooks/useChatInput'
import { useChatInput } from '../../../hooks/useChatInput'
import { useClickOutside } from '../../../hooks/useClickOutside'
import { useChatStore } from '../../../store/chat'
import { useSettingStore } from '../../../store/setting'
import EmotionButton from '../../button/EmotionButton.vue'
import FunctionButton from '../../button/FunctionButton.vue'
import SendIcon from '../../icon/SendIcon.vue'
import EmotionModal from './EmotionModal.vue'
import InputActionMenu from './InputActionMenu.vue'

export default defineComponent({
  name: 'CustomerInputCard',
  components: {
    EmotionButton,
    EmotionModal,
    FunctionButton,
    InputActionMenu,
    SendIcon,
  },
  props: {
    color: {
      type: String,
      default: '#2e9b4a',
    },
  },
  setup(props) {
    const cStore = useChatStore()
    const sStore = useSettingStore()
    const composerRef = ref<HTMLElement | null>(null)

    const hasContent = computed(() => cStore.chatContent.trim().length > 0)

    const {
      textareaRef,
      handleKeydown,
      autoResize,
      sendMessage,
      sendImage,
      sendFile,
      handleCompositionStart,
      handleCompositionEnd,
    } = useChatInput()

    const closePopovers = () => {
      sStore.handleFuncClose()
      sStore.handleEmotionClose()
    }

    const handleSend = () => {
      closePopovers()
      sendMessage()
    }

    const handleSendImage = (data: ChatImageUploadData) => {
      sendImage(data)
    }

    const handleSendFile = (data: ChatFileUploadResult) => {
      sendFile(data)
    }

    const handleEmotion = () => {
      sStore.handleSwitchEmotion()
    }

    const handleFunction = () => {
      sStore.handleSwitchFunc()
    }

    const handleRestart = () => {
      closePopovers()
      cStore.wsConnect()
    }

    watch(
      () => cStore.sendDisable,
      (sendDisabled) => {
        if (!sendDisabled) return
        textareaRef.value?.blur()
        closePopovers()
      },
    )

    useClickOutside(composerRef, [], closePopovers)

    return {
      autoResize,
      cStore,
      closePopovers,
      composerRef,
      handleCompositionEnd,
      handleCompositionStart,
      handleEmotion,
      handleFunction,
      handleKeydown,
      handleRestart,
      handleSend,
      handleSendFile,
      handleSendImage,
      hasContent,
      sStore,
      textareaRef,
    }
  },
})
</script>

<style lang="less" scoped>
.chat-input-card {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  margin: 0 16px 16px;
}

.chat-input-wrapper,
.rechat-pill,
.agent-offline-pill {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-height: 52px;
  padding: 8px 12px;
  align-items: flex-end;
  border: 1px solid var(--border-default, #edefed);
  border-radius: 100px;
  background: #fff;
}

.chat-input-wrapper {
  gap: 8px;
}

.input-control-anchor {
  display: flex;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  align-self: center;
}

.chat-textarea {
  box-sizing: border-box;
  min-width: 0;
  min-height: 32px;
  max-height: 98px;
  padding: 5px 0;
  flex: 1;
  overflow-y: hidden;
  resize: none;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  scrollbar-width: none;

  &::placeholder {
    color: var(--text-4);
    opacity: 1;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.send-button {
  box-sizing: border-box;
  display: grid;
  width: 28px;
  height: 28px;
  padding: 0;
  flex: 0 0 28px;
  align-self: center;
  place-items: center;
  overflow: hidden;
  border: 0;
  border-radius: 50%;
  background: var(--abot-brand-disabled);
  cursor: default;

  &.active {
    background: var(--input-brand-color);
    cursor: pointer;
  }

  &:focus-visible {
    outline: 2px solid var(--input-brand-color);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 1;
  }
}

.rechat-pill {
  justify-content: center;
  gap: 8px;
  align-items: center;

  & > span,
  & > button {
    font-family: inherit;
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
  }

  & > span {
    min-width: 0;
    overflow: hidden;
    color: var(--text-3);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > button {
    padding: 0;
    flex-shrink: 0;
    border: 0;
    background: transparent;
    color: var(--input-brand-color);
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
  }
}

// 客服意外离线时的输入区替换：跟 rechat-pill 同外形，但没有重开会话按钮，
// 仅显示"您已离线，无法处理在线业务"提示。
.agent-offline-pill {
  justify-content: center;
  align-items: center;

  & > span {
    min-width: 0;
    overflow: hidden;
    color: var(--text-3);
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: inherit;
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
  }
}

.chat-input-card :deep(.emotion-modal) {
  top: auto;
  right: 0;
  bottom: calc(100% + 8px);
  left: 0;
  box-sizing: border-box;
  width: 100%;
}

.fade-scale-enter-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.fade-scale-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.98);
}

@media (max-width: 768px) {
  .chat-input-card {
    margin-bottom: max(16px, env(safe-area-inset-bottom, 0px));
  }
}
</style>
